/**
 * Zustand store utilities.
 *
 * Provides common utilities for Zustand stores including DevTools integration
 * and middleware helpers.
 *
 * @module storeUtils
 */

import { devtools } from 'zustand/middleware';

/**
 * Check if we're in development mode
 */
const isDevelopment = import.meta.env.DEV;

/**
 * DevTools configuration for Zustand stores.
 * Only enables DevTools in development mode.
 */
export const devToolsConfig = {
  enabled: isDevelopment,
  name: 'Zustand Store',
};

/**
 * Wrapper for devtools middleware that only applies in development.
 * In production, returns the store creator as-is.
 *
 * @param storeCreator - The store creator function
 * @param storeName - Name of the store for DevTools
 * @returns Store creator with or without devtools middleware
 */
export function withDevTools<T extends (...args: unknown[]) => unknown>(
  storeCreator: T,
  storeName: string,
): T {
  if (!isDevelopment) {
    return storeCreator;
  }

  // Apply devtools middleware (only in development)
  // In production builds, this code is tree-shaken out
  return devtools(storeCreator, {
    name: storeName,
  }) as T;
}

